import type { Glifo } from "./glifos.js";
import { Icone } from "./Icone.js";

/**
 * Uma métrica da faixa: rótulo, valor e nota — **sempre as três**.
 *
 * As três linhas são obrigatórias porque é isso que faz as colunas alinharem
 * entre si: `.metrica` é `subgrid`, e uma métrica sem nota deixaria a vizinha
 * subir. "Nada ainda" é uma nota legítima; ausência de nota não é.
 *
 * Vive aqui, e não copiado em cada tela, por um motivo medido: as três cópias
 * anteriores divergiram, e a do relatório de simulado tinha perdido a classe
 * `etiqueta` no rótulo e na nota — a mesma faixa saía com tipografia diferente
 * conforme a tela. Componente único é o que impede a divergência de voltar.
 *
 * **Todo número da faixa é `.dado`, e não existe exceção** (Etapa 5). A faixa
 * existe para comparar, e comparar exige tratamento igual: mono, tabular,
 * alinhado por coluna. A ofensiva era a exceção — `.numero-ritual` numa célula
 * — e deixou de ser, porque virou chama e saiu da faixa. O número de ritual é
 * **um por tela**, e nesta tela ele é o da chama; se dois números disputarem a
 * cor, ela para de significar alguma coisa.
 *
 * `glifo` é opcional e fica **junto do rótulo**, nunca do valor: o ícone é o
 * que faz a coluna ser achada de relance na faixa de quatro, e encostá-lo no
 * número atrapalharia justamente a comparação que a faixa existe para
 * permitir. Ele é decorativo — o rótulo ao lado já diz a mesma coisa.
 *
 * O peso do ícone segue o papel, e na faixa o papel é sempre estado: nenhuma
 * métrica é ritual.
 *
 * **As três linhas são três papéis, e não três tamanhos do mesmo** (ADR 029). O
 * rótulo é `.etiqueta` — caixa alta, pequena, apagada, abre o bloco. O valor é
 * `--numero-dado`, 3,27× o rótulo, e é o único que se lê de longe. A nota é
 * `.legenda`, caixa normal, menor que o corpo. Ela era uma `.etiqueta` com
 * `text-transform` e `letter-spacing` desfeitos por cima — a mesma classe
 * servindo dois papéis, cada um anulando metade do outro.
 *
 * `unidade` é opcional e fica **colada ao número, na linha de base dele**. Ela
 * existe para o caso que motivou a rodada: a data-alvo mostrava "109" solto sob
 * o rótulo DATA-ALVO, e ninguém entendia que eram dias. Unidade não é nota —
 * a nota diz o contexto ("restam até 31/12"), a unidade diz o que o número mede.
 * Separá-las é o que faz a linha inteira ser lida como uma frase: cento e nove
 * dias, e eles vão até tal data.
 *
 * ---
 *
 * **Duas variantes, e a coluna continua sendo o padrão** (Etapa 7).
 *
 *   `coluna`  as três linhas empilhadas, em `subgrid`. É a faixa de três ou
 *             quatro métricas que se comparam entre si: o relatório do simulado
 *             e o painel de números. Ela não mudou.
 *   `linha`   rótulo à esquerda, campo e nota à direita, tudo numa linha só.
 *             É a coluna de três da home, que divide a faixa com o cartão do
 *             ritual e precisa ter a altura dele.
 *
 * A troca não é de gosto, é de vizinhança. Na faixa, as métricas ficam lado a
 * lado e o que importa é **alinhar as três linhas entre colunas** — daí o
 * `subgrid`. Na coluna da home elas ficam uma sobre a outra, e empilhar rótulo,
 * valor e nota dentro de cada uma somaria nove linhas num bloco de 180px: o que
 * sobrava era espaço vazio à direita de cada rótulo, que foi o defeito que a
 * rodada de design veio consertar.
 *
 * A variante em linha monta um `<span>` a mais em volta do valor e da nota. Ele
 * existe para as duas assentarem na mesma linha de base **entre si**, enquanto o
 * rótulo se centra na altura da linha inteira — três itens soltos num flex só
 * teriam que escolher um alinhamento para os três.
 *
 * `estado` pinta o ícone e acende a borda no hover, e é **classe, nunca `style`
 * inline**: a CSP é `style-src 'self'` sem `style-src-attr` (invariante 17), e
 * cor de estado fora de `tokens.css` contraria o invariante 10 de qualquer
 * forma. Ele é opcional porque nem toda métrica tem estado — "no banco" é uma
 * contagem, não um veredito.
 */
export function Metrica({
  rotulo,
  valor,
  unidade,
  nota,
  glifo,
  variante = "coluna",
  estado,
}: {
  rotulo: string;
  valor: string;
  unidade?: string;
  nota: string;
  glifo?: Glifo;
  variante?: "coluna" | "linha";
  /** Tinge o ícone e a borda de hover. Sem ele a métrica é neutra. */
  estado?: "acerto" | "erro" | "duvida" | "devido";
}) {
  const classes = ["metrica"];
  if (variante === "linha") classes.push("metrica--linha");
  if (estado) classes.push(`metrica--${estado}`);

  const marcaDoRotulo = (
    <p className="secundario metrica__rotulo">
      {glifo && <Icone glifo={glifo} papel="estado" tamanho="p" />}
      {rotulo}
    </p>
  );

  const marcaDoValor = (
    <p className="metrica__valor">
      <span className="dado">{valor}</span>
      {unidade && <span className="metrica__unidade">{unidade}</span>}
    </p>
  );

  const marcaDaNota = <p className="legenda metrica__nota">{nota}</p>;

  if (variante === "linha") {
    return (
      <div className={classes.join(" ")}>
        {marcaDoRotulo}
        <span className="metrica__campo">
          {marcaDoValor}
          {marcaDaNota}
        </span>
      </div>
    );
  }

  return (
    <div className={classes.join(" ")}>
      {marcaDoRotulo}
      {marcaDoValor}
      {marcaDaNota}
    </div>
  );
}
